/**
 * Extract petition ID from various URL formats and plain IDs
 */
export const extractPetitionId = (input) => {
    if (!input) return null;

    // Remove whitespace
    const cleanInput = input.trim();

    // Check if it's just a number (petition ID)
    if (/^\d+$/.test(cleanInput)) {
        const id = cleanInput;
        // Validate reasonable ID length
        if (id.length >= 3 && id.length <= 8) {
            return id;
        }
        return null;
    }

    // Extract from various URL formats
    const patterns = [
        /petition\.parliament\.uk\/petitions\/(\d+)/,
        /\/petitions\/(\d+)/,
        /petitions\/(\d+)/,
        /\/(\d+)$/, // Just /123456 format
    ];

    for (const pattern of patterns) {
        const match = cleanInput.match(pattern);
        if (match && match[1]) {
            const id = match[1];
            // Validate reasonable ID length
            if (id.length >= 3 && id.length <= 8) {
                return id;
            }
        }
    }

    return null;
};

/**
 * Generate a URL-safe slug from a petition title
 */
export const slugify = (title) => {
    if (!title) return '';
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 80);
};

/**
 * Build petition URL from ID
 */
export const buildPetitionUrl = (petitionId) => {
    return `https://petition.parliament.uk/petitions/${petitionId}`;
};

/**
 * Build app URL for petition (with optional slug)
 */
export const buildAppPetitionUrl = (petitionId, title, baseUrl = '') => {
    const slug = title ? slugify(title) : null;
    const path = slug ? `/petition/${petitionId}/${slug}` : `/petition/${petitionId}`;
    return `${baseUrl}${path}`;
};

/**
 * Validate petition input (URL or ID)
 */
export const validatePetitionInput = (input) => {
    if (!input || !input.trim()) {
        return { isValid: false, message: 'Please enter a petition URL or ID', petitionId: null };
    }

    const petitionId = extractPetitionId(input);

    if (!petitionId) {
        return {
            isValid: false,
            message: 'Invalid format. Use either a petition ID (e.g., 727360) or full URL',
            petitionId: null
        };
    }

    // Additional validation for ID reasonableness
    const id = parseInt(petitionId);
    if (id < 100) {
        return {
            isValid: false,
            message: 'Petition ID seems too low',
            petitionId: null
        };
    }

    if (id > 99999999) {
        return {
            isValid: false,
            message: 'Petition ID seems too high',
            petitionId: null
        };
    }

    return {
        isValid: true,
        message: `Valid petition ID: ${petitionId}`,
        petitionId
    };
};

/**
 * Get current page URL for sharing
 */
export const getCurrentShareUrl = () => {
    return window.location.href;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return { success: true };
    } catch (err) {
        // Fallback for older browsers
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return { success: true };
        } catch (fallbackErr) {
            return { success: false, error: fallbackErr.message };
        }
    }
};

/**
 * Share content using Web Share API or fallback to clipboard
 */
export const shareContent = async (shareData) => {
    try {
        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return { success: true, method: 'native' };
        } else {
            // Fallback to copying URL
            const result = await copyToClipboard(shareData.url || shareData.text);
            return {
                success: result.success,
                method: 'clipboard',
                error: result.error
            };
        }
    } catch (err) {
        console.error('Share failed:', err);
        return { success: false, error: err.message, method: 'failed' };
    }
};

/**
 * Generate social media sharing URLs
 */
export const generateSocialShareUrls = (url, title, description) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedDescription = encodeURIComponent(description);

    return {
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
        email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
        reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
    };
};

/**
 * Update page metadata for better sharing
 */
export const updatePageMetadata = (petition) => {
    if (!petition) return;

    // Update page title
    document.title = `${petition.title} - UK Petition Tracker`;

    // Update or create meta tags
    const updateMetaTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`) ||
            document.querySelector(`meta[name="${property}"]`);

        if (!tag) {
            tag = document.createElement('meta');
            if (property.startsWith('og:') || property.startsWith('twitter:')) {
                tag.setAttribute('property', property);
            } else {
                tag.setAttribute('name', property);
            }
            document.head.appendChild(tag);
        }

        tag.setAttribute('content', content);
    };

    const currentUrl = getCurrentShareUrl();
    const description = `${petition.signatureCount?.toLocaleString()} signatures • ${petition.background?.substring(0, 150)}...`;

    // Open Graph tags
    updateMetaTag('og:title', petition.title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', currentUrl);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:site_name', 'UK Petition Tracker');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', petition.title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:url', currentUrl);

    // General meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', `${petition.title}, UK petition, parliament petition, government petition, uk signatures, petition tracker`);

    // Structured data for individual petition
    const schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": petition.title,
        "description": description,
        "url": currentUrl,
        "isPartOf": { "@type": "WebSite", "name": "UK Petition Tracker", "url": "https://ukpetitiontracker.com/" },
        "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ukpetitiontracker.com/" },
                { "@type": "ListItem", "position": 2, "name": "Petition", "item": currentUrl }
            ]
        },
        "mainEntity": {
            "@type": "GovernmentService",
            "name": petition.title,
            "description": petition.background || petition.title,
            "url": `https://petition.parliament.uk/petitions/${petition.id}`,
            "provider": {
                "@type": "GovernmentOrganization",
                "name": "UK Parliament",
                "url": "https://petition.parliament.uk"
            }
        }
    };

    let schemaTag = document.getElementById('petition-schema');
    if (!schemaTag) {
        schemaTag = document.createElement('script');
        schemaTag.type = 'application/ld+json';
        schemaTag.id = 'petition-schema';
        document.head.appendChild(schemaTag);
    }
    schemaTag.text = JSON.stringify(schema);
};

/**
 * Get URL parameters
 */
export const getUrlParams = () => {
    return new URLSearchParams(window.location.search);
};

/**
 * Update URL parameters without page reload
 */
export const updateUrlParams = (params, replace = false) => {
    const url = new URL(window.location);

    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
    });

    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', url);
};

/**
 * Format petition URL for display
 */
export const formatUrlForDisplay = (url, maxLength = 60) => {
    if (!url) return '';

    if (url.length <= maxLength) return url;

    const start = url.substring(0, Math.floor(maxLength / 2) - 2);
    const end = url.substring(url.length - Math.floor(maxLength / 2) + 2);

    return `${start}...${end}`;
};

/**
 * Check if current environment supports sharing features
 */
export const getSharingCapabilities = () => {
    return {
        nativeShare: !!(navigator.share && navigator.canShare),
        clipboard: !!(navigator.clipboard && navigator.clipboard.writeText),
        fullscreen: !!(document.documentElement.requestFullscreen),
        notifications: !!window.Notification,
        serviceWorker: !!navigator.serviceWorker
    };
};